import type { Boat } from "./sim/Boat";

interface BoatHudRefs {
  sog: HTMLSpanElement;
  surge: HTMLSpanElement;
  sway: HTMLSpanElement;
  throttle: HTMLSpanElement;
  rudder: HTMLSpanElement;
  heading: HTMLSpanElement;
  cog: HTMLSpanElement;
  tws: HTMLSpanElement;
  twd: HTMLSpanElement;
  aws: HTMLSpanElement;
  awa: HTMLSpanElement;
  leeway: HTMLSpanElement;
  pos: HTMLSpanElement;
}

const KNOTS_PER_MPS = 1.943844492;

function mkLine(parent: HTMLElement, label: string): HTMLSpanElement {
  const line = document.createElement("div");
  line.style.display = "flex";
  line.style.justifyContent = "space-between";
  line.style.gap = "12px";

  const l = document.createElement("span");
  l.textContent = label;
  l.style.color = "#8fb6ff";
  l.style.opacity = "0.9";

  const v = document.createElement("span");
  v.textContent = "--";
  v.style.color = "#f2f6ff";

  line.append(l, v);
  parent.appendChild(line);
  return v;
}

function norm360(deg: number): number {
  const d = deg % 360;
  return d < 0 ? d + 360 : d;
}

/**
 * Creates a compact fixed HUD in the lower-left corner.
 * Returns an updater closure that should be called every frame.
 */
export function createBoatHud(
  boat: Boat,
  parent: HTMLElement = document.body,
): (windX: number, windZ: number) => void {
  const root = document.createElement("div");
  root.id = "boat-hud";
  root.style.position = "fixed";
  root.style.left = "14px";
  root.style.bottom = "14px";
  root.style.minWidth = "252px";
  root.style.maxWidth = "340px";
  root.style.padding = "10px 12px";
  root.style.border = "1px solid rgba(175, 205, 255, 0.32)";
  root.style.borderRadius = "10px";
  root.style.background = "rgba(10, 15, 28, 0.62)";
  root.style.backdropFilter = "blur(4px)";
  root.style.color = "#eaf1ff";
  root.style.fontFamily = '"Consolas", "Menlo", "Monaco", monospace';
  root.style.fontSize = "12px";
  root.style.lineHeight = "1.35";
  root.style.zIndex = "8";
  root.style.pointerEvents = "none";
  // Hidden by default — toggled by the service button in the top-right.
  root.style.display = "none";

  // Floating toggle button in the top-right (mirrors the lil-gui placement
  // but offset to the left so it doesn't overlap when the panel is open).
  const toggle = document.createElement("button");
  toggle.id = "boat-hud-toggle";
  toggle.type = "button";
  toggle.title = "Toggle telemetry HUD";
  toggle.textContent = "HUD";
  toggle.style.position = "fixed";
  toggle.style.top = "8px";
  toggle.style.right = "320px";
  toggle.style.padding = "6px 10px";
  toggle.style.border = "1px solid rgba(175, 205, 255, 0.32)";
  toggle.style.borderRadius = "8px";
  toggle.style.background = "rgba(10, 15, 28, 0.62)";
  toggle.style.backdropFilter = "blur(4px)";
  toggle.style.color = "#eaf1ff";
  toggle.style.fontFamily = '"Consolas", "Menlo", "Monaco", monospace';
  toggle.style.fontSize = "11px";
  toggle.style.letterSpacing = "0.08em";
  toggle.style.cursor = "pointer";
  toggle.style.zIndex = "9";
  toggle.style.userSelect = "none";
  toggle.addEventListener("click", () => {
    const visible = root.style.display !== "none";
    root.style.display = visible ? "none" : "block";
    toggle.style.borderColor = visible
      ? "rgba(175, 205, 255, 0.32)"
      : "rgba(175, 205, 255, 0.85)";
  });
  parent.appendChild(toggle);

  const title = document.createElement("div");
  title.textContent = "BOAT HUD";
  title.style.fontSize = "11px";
  title.style.letterSpacing = "0.08em";
  title.style.color = "#b9ccff";
  title.style.marginBottom = "8px";
  root.appendChild(title);

  const refs: BoatHudRefs = {
    sog: mkLine(root, "SOG"),
    surge: mkLine(root, "SURGE"),
    sway: mkLine(root, "SWAY"),
    throttle: mkLine(root, "THROTTLE"),
    rudder: mkLine(root, "RUDDER"),
    heading: mkLine(root, "HDG"),
    cog: mkLine(root, "COG"),
    tws: mkLine(root, "TWS"),
    twd: mkLine(root, "TWD"),
    aws: mkLine(root, "AWS"),
    awa: mkLine(root, "AWA"),
    leeway: mkLine(root, "LEEWAY"),
    pos: mkLine(root, "POS XZ"),
  };

  parent.appendChild(root);

  return (windX: number, windZ: number): void => {
    // Skip number formatting work while hidden — pure overhead otherwise.
    if (root.style.display === "none") return;
    const ch = Math.cos(boat.heading);
    const sh = Math.sin(boat.heading);
    const fwdX = sh;
    const fwdZ = -ch;
    const rgtX = ch;
    const rgtZ = sh;

    const vx = boat.surge * fwdX + boat.sway * rgtX;
    const vz = boat.surge * fwdZ + boat.sway * rgtZ;
    const sog = Math.hypot(vx, vz);

    const appX = windX - vx;
    const appZ = windZ - vz;
    const aws = Math.hypot(appX, appZ);
    const appBodyX = appX * fwdX + appZ * fwdZ;
    const appBodyZ = appX * rgtX + appZ * rgtZ;
    const awaDeg = (Math.atan2(appBodyZ, appBodyX) * 180) / Math.PI;

    // Compass bearings: N (=−Z) is 0°, E (=+X) is 90°, clockwise from N.
    const hdgDeg = norm360((Math.atan2(fwdX, -fwdZ) * 180) / Math.PI);
    const cogDeg = norm360((Math.atan2(vx, -vz) * 180) / Math.PI);
    const twdDeg = norm360((Math.atan2(windX, -windZ) * 180) / Math.PI);

    refs.sog.textContent = `${sog.toFixed(2)} m/s  (${(sog * KNOTS_PER_MPS).toFixed(2)} kn)`;
    refs.surge.textContent = `${boat.surge.toFixed(2)} m/s`;
    refs.sway.textContent = `${boat.sway.toFixed(2)} m/s`;
    refs.throttle.textContent = `${(boat.throttle * 100).toFixed(0)} %`;
    refs.rudder.textContent = `${((boat.rudderAngle * 180) / Math.PI).toFixed(1)} deg`;
    refs.heading.textContent = `${hdgDeg.toFixed(1)} deg`;
    refs.cog.textContent = `${cogDeg.toFixed(1)} deg`;
    refs.tws.textContent = `${Math.hypot(windX, windZ).toFixed(2)} m/s`;
    refs.twd.textContent = `${twdDeg.toFixed(1)} deg`;
    refs.aws.textContent = `${aws.toFixed(2)} m/s`;
    refs.awa.textContent = `${awaDeg.toFixed(1)} deg`;
    refs.leeway.textContent = `${((boat.leeway * 180) / Math.PI).toFixed(1)} deg`;
    refs.pos.textContent = `${boat.posX.toFixed(1)}, ${boat.posZ.toFixed(1)}`;
  };
}
